import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { HistogramIcon } from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { ToolManager } from '~/engine/tool/ToolManager';
import {
    histogramHintEnabledState,
    scaleDisplayEnabledState,
} from '~/state/atoms/toolsAtoms';

export default function WindowingHistogramTool() {
    const intl = useIntl();
    const [enabled, setEnabled] = useRecoilState(histogramHintEnabledState);
    const setScaleDisplayEnabled = useSetRecoilState(scaleDisplayEnabledState);
    const tooltip = intl.formatMessage({ id: 'toolbar.histogram.tooltip' });

    // Hold the latest `enabled` so the keydown listener below can read it
    // without being in the effect's dependency list — letting the listener
    // mount once instead of re-subscribing on every toggle.
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    // Ruler and histogram share the bottom band; enabling the histogram turns
    // the ruler off so toggling always has a visible effect. Single source of
    // truth for both the toolbar click and the `H` hotkey.
    const toggle = useCallback(() => {
        const next = !enabledRef.current;
        enabledRef.current = next;
        setEnabled(next);
        if (next) {
            setScaleDisplayEnabled(false);
        }
    }, [setEnabled, setScaleDisplayEnabled]);

    // Plain `H` toggles the histogram. Same guard pattern as the A/C/S plane
    // selection: skip when any modifier is held (so Ctrl+H still routes to
    // sensitive-info toggle), on auto-repeat, when focus is in a text input,
    // or while a tool action is in progress. Only one toolbar step renders
    // at a time, so there is no listener duplication across steps.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                event.shiftKey
            ) {
                return;
            }
            if (event.repeat) {
                return;
            }
            if (event.key.toLowerCase() !== 'h') {
                return;
            }
            const activeElement = document.activeElement as HTMLElement | null;
            if (
                activeElement &&
                (activeElement.tagName === 'INPUT' ||
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.isContentEditable)
            ) {
                return;
            }
            if (ToolManager.getActiveTool()?.isActionInProgress()) {
                return;
            }
            toggle();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [toggle]);

    return (
        <ToolbarButton
            icon={<HistogramIcon />}
            tooltip={tooltip}
            tooltipPlacement="bottom"
            selected={enabled}
            ariaPressed={enabled}
            onClick={toggle}
        />
    );
}
